import { Node } from "react-checkbox-tree";

import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { Configuration, GroupConfig } from "fond/types";
import { LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";

import { Row } from "./LayerStyleTree.styles";

/**
 * Generates an array of nodes representing a hierarchical tree structure of groups, layers, sublayers, and styles
 * to be displayed in a checkbox tree. Each node represents a configuration entity (group, layer, sublayer, or style)
 * with its children nested underneath.
 *
 * @param config - The version configuration containing all entities (groups, layers, sublayers, styles)
 * @param excludedKeys - Array of layer/sublayer keys to exclude from the tree
 * @returns An array of tree nodes suitable for react-checkbox-tree, with icons for layers, sublayers, and styles.
 */
export const generateLayerStyleTreeItems = ({ config, excludedKeys }: { config: Configuration; excludedKeys: string[] }): Array<Node> => {
  const items: Node[] = [];

  const add = (item: GroupConfig | LayerConfig | SublayerConfig | undefined): Node | null => {
    if (!item || (item.Key && excludedKeys.includes(item.Key))) return null;

    // Add styles as children
    const styleNodes: Node[] = [];
    if (item.Type === "LAYER" || item.Type === "SUBLAYER") {
      item.Styles.forEach((styleId) => {
        const style = config.Data.entities[styleId] as LayerStyle;
        const styleItem: Node = {
          value: style.ID,
          label: (
            <Row>
              <InferredIcon entity={style} config={config} />
              {style.Name}
            </Row>
          ),
          className: "STYLE",
        };
        styleNodes.push(styleItem);
      });
    }

    const children = item.Type !== "SUBLAYER" ? item.Children : [];
    if (item && item.Key) {
      const entity = config.Data.entities[item.ID];
      const newItem: Node = {
        value: item.ID,
        label: (
          <Row>
            {entity && entity.Type !== "GROUP" && <InferredIcon entity={entity} config={config} />}
            {item.Label}
          </Row>
        ),
        className: item.Type,
        children: styleNodes,
      };

      children.forEach((id) => {
        const childEntity = config.Data.entities[id];
        const child = childEntity && childEntity?.Type !== "STYLE" ? add(childEntity) : null;
        if (child) newItem.children?.push(child);
      });

      return newItem;
    }
    return null;
  };

  config.MapChildren.forEach((childId) => {
    const item = config.Data.entities[childId];
    if (item && item.Type !== "STYLE") {
      const node = add(item);
      if (node) items.push(node);
    }
  });

  return items;
};
